/**
 * Positioning System - Single Source of Truth
 *
 * All memorial positioning is calculated here.
 * Categories define zones, and entries are scattered within zones
 * using a deterministic algorithm based on entry properties.
 */

// ============================================
// SECTION/CATEGORY DEFINITIONS
// ============================================

export const CATEGORIES = {
  FAMILIES: {
    id: 'families',
    sectionId: 'section-1',
    label: 'Remembering Families',
    order: 1,
    // Zone boundaries (percentage of section area)
    desktop: { xMin: 10, xMax: 90, yMin: 10, yMax: 90 },
    mobile: { xMin: 12, xMax: 88, yMin: 10, yMax: 85 }
  },
  YOUNG_LIVES: {
    id: 'young_lives',
    sectionId: 'section-2',
    label: 'Young Lives Lost',
    order: 2,
    desktop: { xMin: 10, xMax: 90, yMin: 10, yMax: 90 },
    mobile: { xMin: 12, xMax: 88, yMin: 10, yMax: 85 }
  },
  COMMUNITY_LEADERS: {
    id: 'community_leaders',
    sectionId: 'section-3',
    label: 'Community Leaders',
    order: 3,
    desktop: { xMin: 10, xMax: 90, yMin: 10, yMax: 90 },
    mobile: { xMin: 12, xMax: 88, yMin: 10, yMax: 85 }
  },
  HOSTAGES_RETURNED: {
    id: 'hostages_returned',
    sectionId: 'section-4',
    label: 'Hostages Returned',
    order: 4,
    desktop: { xMin: 10, xMax: 90, yMin: 8, yMax: 92 },
    mobile: { xMin: 12, xMax: 88, yMin: 8, yMax: 92 }
  },
  FULL_MEMORIAL: {
    id: 'full_memorial',
    sectionId: 'section-3', // Grouped with community leaders
    label: 'Full Memorial',
    order: 99,
    // Fixed position (not scattered)
    desktop: { xMin: 55, xMax: 65, yMin: 80, yMax: 88 },
    mobile: { xMin: 45, xMax: 65, yMin: 75, yMax: 85 }
  }
};

// Map category string IDs to CATEGORIES objects
export const CATEGORY_BY_ID = {
  'families': CATEGORIES.FAMILIES,
  'young-lives': CATEGORIES.YOUNG_LIVES,
  'young_lives': CATEGORIES.YOUNG_LIVES,
  'community-leaders': CATEGORIES.COMMUNITY_LEADERS,
  'community_leaders': CATEGORIES.COMMUNITY_LEADERS,
  'hostages-returned': CATEGORIES.HOSTAGES_RETURNED,
  'hostages_returned': CATEGORIES.HOSTAGES_RETURNED,
  'special': CATEGORIES.FULL_MEMORIAL,
  'full_memorial': CATEGORIES.FULL_MEMORIAL
};

// ============================================
// SECTION CONFIGURATION (for UI)
// ============================================

export const SECTIONS = [
  { id: 'section-1', title: 'Remembering Families', categoryId: 'families' },
  { id: 'section-2', title: 'Young Lives Lost', categoryId: 'young_lives' },
  { id: 'section-3', title: 'Community Leaders', categoryId: 'community_leaders' },
  { id: 'section-4', title: 'Hostages Returned', categoryId: 'hostages_returned' }
];

// ============================================
// POSITIONING ALGORITHMS
// ============================================

/**
 * Generate a deterministic hash from a string
 * Used for consistent pseudo-random positioning based on name
 */
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
};

/**
 * Deterministic pseudo-random number generator
 * Returns value between 0 and 1 based on seed
 */
const seededRandom = (seed) => {
  const x = Math.sin(seed * 9999) * 10000;
  return x - Math.floor(x);
};

/**
 * Scatter distribution - spreads entries across zone with natural randomness
 * Uses name-based seeding for deterministic but organic-looking placement
 */
const scatterDistribution = (index, total, zone, name) => {
  const hash = hashString(name);

  // Divide zone into regions to ensure spread
  // More entries = more regions
  const cols = Math.ceil(Math.sqrt(total * 1.5));
  const rows = Math.ceil(total / cols);

  // Assign each entry to a region based on index
  const col = index % cols;
  const row = Math.floor(index / cols);

  // Calculate region boundaries
  const regionWidth = (zone.xMax - zone.xMin) / cols;
  const regionHeight = (zone.yMax - zone.yMin) / rows;

  // Base position is center of region
  const baseX = zone.xMin + (col + 0.5) * regionWidth;
  const baseY = zone.yMin + (row + 0.5) * regionHeight;

  // Add deterministic randomness within region (±40% of region size)
  const jitterX = (seededRandom(hash) - 0.5) * regionWidth * 0.8;
  const jitterY = (seededRandom(hash * 2) - 0.5) * regionHeight * 0.8;

  // Calculate final position with jitter
  let x = baseX + jitterX;
  let y = baseY + jitterY;

  // Clamp to zone boundaries with margin
  const margin = 5;
  x = Math.max(zone.xMin + margin, Math.min(zone.xMax - margin, x));
  y = Math.max(zone.yMin + margin, Math.min(zone.yMax - margin, y));

  return {
    x: Math.round(x * 10) / 10,
    y: Math.round(y * 10) / 10
  };
};

/**
 * Calculate positions for all entries in a category
 * @param {Array} entries - Array of memorial entries
 * @param {Object} category - Category definition from CATEGORIES
 * @param {string} layout - 'desktop' or 'mobile'
 * @returns {Array} Entries with calculated positions
 */
export const calculatePositions = (entries, category, layout = 'desktop') => {
  const zone = category[layout];

  return entries.map((entry, index) => {
    const position = scatterDistribution(index, entries.length, zone, entry.name);

    return {
      ...entry,
      position
    };
  });
};

/**
 * Get category for an entry based on its category string
 */
export const getCategoryForEntry = (entry) => {
  if (entry.category) {
    return CATEGORY_BY_ID[entry.category] || CATEGORIES.COMMUNITY_LEADERS;
  }

  // Fallback logic based on ID ranges
  if (entry.id >= 27 && entry.id <= 44) {
    return CATEGORIES.HOSTAGES_RETURNED;
  }
  if (entry.id === 4) {
    return CATEGORIES.FULL_MEMORIAL;
  }

  return CATEGORIES.COMMUNITY_LEADERS;
};

/**
 * Group entries by their section
 */
export const groupEntriesBySection = (entries) => {
  const groups = {};

  SECTIONS.forEach(section => {
    groups[section.id] = [];
  });

  entries.forEach(entry => {
    const category = getCategoryForEntry(entry);
    const sectionId = category.sectionId;
    if (groups[sectionId]) {
      groups[sectionId].push(entry);
    }
  });

  return groups;
};

export default {
  CATEGORIES,
  CATEGORY_BY_ID,
  SECTIONS,
  calculatePositions,
  getCategoryForEntry,
  groupEntriesBySection,
  hashString
};
